const STORAGE_KEY = 'poker-audio-v1';

export class AudioDirector {
  constructor() {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    this.musicEnabled = saved.musicEnabled ?? true;
    this.sfxEnabled = saved.sfxEnabled ?? true;
    this.ctx = null;
    this.musicTracks = new Map();
    this.musicMood = 'base';
    this.activeMusic = null;
    this.fadeTimer = null;
    this.unlocked = false;
    this.unlockPromise = null;
    this.buffers = new Map();
  }

  unlock() {
    if (this.unlocked) return Promise.resolve();
    if (this.unlockPromise) return this.unlockPromise;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return Promise.resolve();
    try {
      this.ctx = new AudioContextClass();
    } catch {
      this.ctx = null;
      return Promise.resolve();
    }
    this.unlockPromise = this.ctx.resume().then(() => {
      this.unlocked = true;
      [
        ['base', './audio/poker-lanterns-lies-silver.mp3'],
        ['hard', './audio/poker-last-coins-on-the-felt.mp3'],
        ['high', './audio/poker-railroad-baron-stakes.mp3']
      ].forEach(([name, url]) => {
        const track = new Audio(url);
        track.loop = true;
        track.preload = 'auto';
        track.volume = 0;
        track.addEventListener('error', () => { track.dataset.failed = 'true'; }, { once: true });
        this.musicTracks.set(name, track);
      });
      this.applyMusicMood(true);
      void Promise.all([
        this.preload('deal', './audio/sfx/card-deal.mp3'),
        this.preload('draw', './audio/sfx/card-shuffle.mp3'),
        this.preload('chip', './audio/sfx/coin-wager.mp3'),
        this.preload('win', './audio/sfx/saloon-win.mp3'),
        this.preload('leave', './audio/sfx/patron-exit.mp3'),
        this.preload('robbery', './audio/sfx/saloon-robbery.mp3')
      ]);
    }).catch(() => {
      this.ctx = null;
      this.unlocked = false;
      this.unlockPromise = null;
    });
    return this.unlockPromise;
  }

  async preload(name, url) {
    try {
      const response = await fetch(url);
      if (!response.ok) return;
      this.buffers.set(name, await this.ctx.decodeAudioData(await response.arrayBuffer()));
    } catch { /* synthesized fallbacks remain available */ }
  }

  persist() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ musicEnabled: this.musicEnabled, sfxEnabled: this.sfxEnabled }));
  }

  setMusic(enabled) {
    this.musicEnabled = enabled;
    this.persist();
    this.applyMusicMood();
  }

  setMood(mood) {
    this.musicMood = ['base', 'hard', 'high'].includes(mood) ? mood : 'base';
    this.applyMusicMood();
  }

  applyMusicMood(immediate = false) {
    if (!this.unlocked || !this.musicTracks.size) return;
    clearInterval(this.fadeTimer);
    if (!this.musicEnabled) {
      this.musicTracks.forEach(track => { track.pause(); track.volume = 0; });
      this.activeMusic = null;
      return;
    }
    const desired = this.musicTracks.get(this.musicMood);
    const target = desired?.dataset.failed === 'true' ? this.musicTracks.get('base') : desired;
    if (!target) return;
    const previous = this.activeMusic;
    const targetVolume = { base: 0.24, hard: 0.19, high: 0.3 }[this.musicMood] ?? 0.24;
    target.play().catch(() => {});
    if (immediate || previous === target) {
      if (previous && previous !== target) { previous.pause(); previous.volume = 0; }
      target.volume = targetVolume;
      this.activeMusic = target;
      return;
    }
    target.volume = 0;
    let step = 0;
    const previousVolume = previous?.volume || 0;
    this.fadeTimer = setInterval(() => {
      step += 1;
      const progress = Math.min(1, step / 18);
      target.volume = targetVolume * progress;
      if (previous && previous !== target) previous.volume = previousVolume * (1 - progress);
      if (progress >= 1) {
        clearInterval(this.fadeTimer);
        if (previous && previous !== target) previous.pause();
        this.activeMusic = target;
      }
    }, 55);
  }

  setSfx(enabled) {
    this.sfxEnabled = enabled;
    this.persist();
  }

  tone(kind = 'chip', frequency = 220, duration = 0.08, gain = 0.055) {
    if (!this.unlocked || !this.sfxEnabled) return;
    const osc = this.ctx.createOscillator();
    const amp = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();
    const now = this.ctx.currentTime;
    const settings = {
      card: ['triangle', 260, 0.045], chip: ['sine', 740, 0.055], fold: ['sawtooth', 120, 0.07], win: ['triangle', 330, 0.13], seat: ['sine', 92, 0.03], robbery: ['sawtooth', 82, 0.11]
    }[kind] || ['sine', frequency, gain];
    osc.type = settings[0];
    osc.frequency.setValueAtTime(settings[1] || frequency, now);
    if (kind === 'win') osc.frequency.exponentialRampToValueAtTime(660, now + duration);
    filter.type = 'lowpass';
    filter.frequency.value = kind === 'chip' ? 2100 : 1100;
    amp.gain.setValueAtTime(settings[2] || gain, now);
    amp.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    osc.connect(filter).connect(amp).connect(this.ctx.destination);
    osc.start(now);
    osc.stop(now + duration);
  }

  playBuffer(name) {
    if (!this.unlocked || !this.sfxEnabled || !this.buffers.has(name)) return false;
    const source = this.ctx.createBufferSource();
    const gain = this.ctx.createGain();
    source.buffer = this.buffers.get(name);
    gain.gain.value = { deal: 0.58, draw: 0.46, chip: 0.48, win: 0.5, leave: 0.45, robbery: 0.58 }[name] ?? 0.46;
    source.connect(gain).connect(this.ctx.destination);
    source.start();
    return true;
  }

  cue(name) {
    const sourceName = name === 'card' || name === 'fold' ? 'deal' : name;
    if (this.playBuffer(sourceName)) return;
    const timing = { deal: ['card', 300, 0.07], chip: ['chip', 700, 0.08], fold: ['fold', 110, 0.12], win: ['win', 330, 0.34], draw: ['card', 420, 0.1], robbery: ['robbery', 82, 0.42] }[name];
    if (timing) this.tone(...timing);
  }
}
